import React from 'react';
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router';
import useGetPackager from '../hooks/packager/useGetPackager';
import { useGetPackages } from '../hooks/packages/useGetPackages';
import PackageTableRow from './PackageTableRow';
import TableButtonsHeader from '../common/TableButtonsHeader';
import { standardTableStyles } from '../common/tableStyles';
import RefreshButton from '../common/Buttons/RefreshButton';
import { useAtomValue } from 'jotai';
import { packagesRefreshUntilAtom } from '../../atoms/packager-atoms';
import { PackageStatuses } from '../../types/packager-types';

const PACKAGES_POLL_MS = 5000;

const PackagesTable: React.FC = () => {
  const { dpid } = useParams<{ dpid: string }>();
  const { packager, isLoading, isFetching, isError } = useGetPackager(dpid!);

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const refreshUntil = useAtomValue(packagesRefreshUntilAtom);

  const page = Number(searchParams.get('page')) || 0;
  const perPage = Number(searchParams.get('perPage')) || 25;

  const {
    data: packages,
    isLoading: isLoadingPackages,
    isError: isErrorPackages,
    isFetching: isFetchingPackages,
    refetch: refetchPackages,
    // advanced=true so each package carries its `completeness` block (the list
    // serializer omits it otherwise) — the completeness chip paints from this.
  } = useGetPackages(
    { advanced: true, limit: perPage, offset: page * perPage },
    {
      // Re-render and create-from finish in a background task: keep polling while
      // a visible package is PROCESSING, or a create-from was just started.
      refetchInterval: (query) =>
        query.state.data?.results?.some((p) => p.status === PackageStatuses.PROCESSING) ||
        Date.now() < refreshUntil
          ? PACKAGES_POLL_MS
          : false,
    }
  );

  const navigate = useNavigate();

  if (isError) {
    return <div>Error loading packager data</div>;
  }

  return (
    <div style={{ padding: '10px 0' }}>
      <TableButtonsHeader justifyContent="end">
        <RefreshButton
          cypressId="button-refresh"
          disabled={isLoadingPackages || isFetchingPackages}
          isLoading={isLoadingPackages || isFetchingPackages}
          onClick={() => refetchPackages()}
        />
      </TableButtonsHeader>
      <Box sx={standardTableStyles.containerSx}>
        <Table sx={standardTableStyles.tableSx}>
          <TableHead sx={standardTableStyles.tableHeadSx}>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Documents</TableCell>
              <TableCell align="center">Completeness</TableCell>
              <TableCell align="center">Created</TableCell>
              <TableCell align="center">Updated</TableCell>
              <TableCell align="center">Delivery Schedule</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-cy="extraction-batch-table-body" sx={standardTableStyles.tableBodySx}>
            {isLoading || isLoadingPackages
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton animation="wave" variant="rounded" height={22} width={64} sx={{ borderRadius: '6px' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={48} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={32} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={22} width={72} sx={{ borderRadius: '6px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={[96, 88, 104, 92, 100][i]} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={[96, 88, 104, 92, 100][i]} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={22} width={72} sx={{ borderRadius: '6px', mx: 'auto' }} /></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : null}

            {!isLoading && !isLoadingPackages && (packages?.count ?? 0) > 0
              ? packages?.results?.map((p: any) => <PackageTableRow key={p.id} p={p} />)
              : null}

            {(packages?.results?.length ?? 0) === 0 &&
            !isFetching &&
            !isFetchingPackages &&
            !isLoading &&
            !isLoadingPackages ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                    No packages found
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        {(packages?.count ?? 0) > 0 && <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={packages?.count ?? 0}
          rowsPerPage={perPage}
          page={page}
          onPageChange={(e, p) => {
            e?.preventDefault();
            searchParams.set('page', p.toString());
            navigate({
              search: searchParams.toString(),
            });
          }}
          onRowsPerPageChange={(e: any) => {
            searchParams.set('perPage', e.target.value);
            searchParams.set('page', '0');
            navigate({
              search: searchParams.toString(),
            });
          }}
        />}
      </Box>
    </div>
  );
};

export default PackagesTable;
